import {useMemo} from 'react'
import type {Markers} from '../stores/slices/markers'
import type {DiffMarkers, PullRequestFileTreeDiff, ThreadMarker} from '../page-data/payloads/file-tree'
import type {ThreadPositioning} from '@github-ui/diff-lines/conversation-types'

/**
 * Stitches thread subjects into thread markers by parsing subject information from different sources.
 *
 * This is a workaround needed because suggested changes require thread subjects, but the data
 * comes from separate sources. In the future, this logic should be moved to build time when
 * thread previews are removed and state should be stored in Zustand.
 *
 * @param initialMarkers - The markers object containing threads that need subject information
 * @param initialDiffSummaries - Array of diff summaries containing marker maps with thread data

 * @returns void - This hook mutates the initialMarkers object directly as a side effect
 *
 * @example
 * ```typescript
 * useStitchSubjectIntoThreads(markers, diffSummaries);
 * ```
 */
export function useStitchSubjectIntoThreads(
  initialMarkers: Markers | undefined,
  initialDiffSummaries: PullRequestFileTreeDiff[],
): void {
  // eslint-disable-next-line react-hooks/void-use-memo
  useMemo(() => {
    if (!initialMarkers || !initialDiffSummaries) return

    for (const diffSummary of initialDiffSummaries) {
      if (!diffSummary.markersMap) continue

      for (const markerKey in diffSummary.markersMap) {
        const diffSummaryMarker: DiffMarkers | undefined = diffSummary.markersMap[markerKey]
        if (!diffSummaryMarker?.threads) continue

        for (const threadMarker of diffSummaryMarker.threads) {
          const positioning = getPositioningValuesFromThreadMarkers(markerKey, threadMarker, diffSummary.path)
          const threadId = Number(threadMarker.id)
          const thread = initialMarkers.threads[threadId]
          if (thread && positioning) {
            const {isOutdated, ...positioningWithoutOutdated} = positioning
            // TODO: This is a workaround to avoid the ESLint error about mutating props directly.
            // Ideally, we should refactor this to avoid direct mutation, but since we need to do a quick fix for performance, this mimics the previous behavior.
            // Object is possibly frozen or sealed, create a new object with the stitched properties
            // eslint-disable-next-line react-hooks/immutability
            initialMarkers.threads[threadId] = {
              ...thread,
              positioning,
              subject: positioningWithoutOutdated,
            }
          }
        }
      }
    }
  }, [initialDiffSummaries, initialMarkers])
}

const diffSideMap = {
  L: 'LEFT',
  R: 'RIGHT',
} as const

type LineSideLetter = 'L' | 'R'

function isLineSideLetter(value: string | undefined): value is LineSideLetter {
  return value === 'L' || value === 'R'
}

function getPositioningValuesFromThreadMarkers(
  diffSummaryKey: string,
  threadMarker: ThreadMarker,
  diffSummaryPath: string,
): ThreadPositioning | undefined {
  if (diffSummaryKey === 'FILE') {
    return undefined
  }

  const endDiffSideChar = diffSummaryKey[0]
  if (!isLineSideLetter(endDiffSideChar)) {
    throw new Error(`Invalid diff side character: ${endDiffSideChar}`)
  }
  const endDiffSideLetter = endDiffSideChar
  const endLineNumber = Number(diffSummaryKey.slice(1))

  let startLineNumber = -1
  let startDiffSideLetter: LineSideLetter = 'R'

  if (threadMarker.start) {
    startLineNumber = parseInt(threadMarker.start.slice(1))
    const startChar = threadMarker.start[0]
    if (isLineSideLetter(startChar)) {
      startDiffSideLetter = startChar
    }
  } else {
    startLineNumber = endLineNumber
    startDiffSideLetter = endDiffSideLetter
  }

  const endDiffSide = diffSideMap[endDiffSideLetter]
  const startDiffSide = diffSideMap[startDiffSideLetter]

  return {
    endLine: endLineNumber,
    endDiffSide,
    path: diffSummaryPath,
    startLine: startLineNumber,
    startDiffSide,
    isOutdated: !!threadMarker.outdatedReason,
  }
}
